//frontend/src/components/AdminRequests/AdminRequestsList.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { authFetch } from "../api/api";
import {
  createRequestSelection,
  getCurrentUser,
  getRequestSelections,
  uploadDocument,
} from "../api/requestSelection/requestSelectionApi";

import {
  Modal,
  StatusBadge,
  STATUS_OPTIONS,
  isAdmin,
} from "./constants";
import "./AdminRequests.css";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

function CreateRequestModal({ onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [me, setMe] = useState(null);
  const [clientId, setClientId] = useState("");
  const [searchManagerId, setSearchManagerId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      getCurrentUser(),
      authFetch(`${BASE_URL}/api/users/`).then((r) => r.json()),
      authFetch(`${BASE_URL}/api/clients/`).then((r) => r.json()),
    ])
      .then(([user, allUsers, allClients]) => {
        setMe(user);
        setUsers(Array.isArray(allUsers) ? allUsers : []);
        setClients(Array.isArray(allClients) ? allClients : []);
      })
      .catch(() => {});
  }, []);

  const searchManagers = users.filter((u) =>
    (u.groups || []).some((g) => g.name === "procurement")
  );

  const addFiles = (selected) => {
    const arr = Array.from(selected).map((file) => ({ file, comment: "" }));
    setFiles((prev) => [...prev, ...arr]);
  };

  const removeFile = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const setFileComment = (idx, comment) =>
    setFiles((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, comment } : item))
    );

  const submit = async (e) => {
    e.preventDefault();
    if (!me?.id) return setError("Не удалось определить текущего пользователя");
    if (!clientId) return setError("Выберите клиента");
    if (!searchManagerId) return setError("Выберите менеджера поиска");

    setSubmitting(true);
    setError(null);
    try {
      const created = await createRequestSelection({
        client_manager_id: me.id,
        client_id: Number(clientId),
        search_manager_id: Number(searchManagerId),
        title: title.trim(),
        description,
      });

      for (const item of files) {
        try {
          await uploadDocument({
            request: created.id,
            file: item.file,
            name: item.file.name,
            comment: item.comment,
          });
        } catch (err) {
          console.error("upload failed", item.file.name, err);
        }
      }

      onCreated(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Новая заявка на поиск" onClose={onClose} width={600}>
      <form onSubmit={submit} className="proc-modal__form">
        <label className="proc-modal__label">
          Клиент *
          <select
            className="proc-input"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            required
          >
            <option value="">— выберите клиента —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.tracking_code ? `(${c.tracking_code})` : ""}
              </option>
            ))}
          </select>
        </label>

        <label className="proc-modal__label">
          Название (товар)
          <input
            className="proc-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            placeholder="Краткое название заявки (до 100 символов)"
          />
        </label>

        <label className="proc-modal__label">
          Менеджер по поиску *
          <select
            className="proc-input"
            value={searchManagerId}
            onChange={(e) => setSearchManagerId(e.target.value)}
            required
          >
            <option value="">— выберите —</option>
            {searchManagers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.username}
              </option>
            ))}
          </select>
        </label>

        {searchManagers.length === 0 && (
          <div className="proc-modal__error">
            Нет пользователей в группе «procurement». Добавьте их в разделе
            «Пользователи».
          </div>
        )}

        <label className="proc-modal__label">
          Описание
          <textarea
            className="proc-input proc-textarea"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div className="proc-modal__label">
          <span>Документы</span>
          <input
            type="file"
            multiple
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          {files.map((item, idx) => (
            <div key={idx} className="proc-modal__file">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  📎 {item.file.name}
                </div>
                <input
                  type="text"
                  className="proc-input"
                  placeholder="Комментарий к файлу"
                  value={item.comment}
                  onChange={(e) => setFileComment(idx, e.target.value)}
                  style={{ marginTop: 4, width: "100%" }}
                />
              </div>
              <button
                type="button"
                className="proc-icon-button"
                style={{ color: "#c23d67" }}
                onClick={() => removeFile(idx)}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {error && <div className="proc-modal__error">{error}</div>}

        <div className="proc-modal__actions">
          <button
            type="button"
            className="proc-button proc-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Отмена
          </button>
          <button type="submit" className="proc-button" disabled={submitting}>
            {submitting ? "Создание..." : "Создать"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function AdminRequestsList() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await getRequestSelections();
      setRequests(items);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    getCurrentUser()
      .then((u) => setCanManage(isAdmin(u)))
      .catch(() => setCanManage(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesSearch =
        !q ||
        String(r.number || "").toLowerCase().includes(q) ||
        String(r.title || "").toLowerCase().includes(q) ||
        String(r.description || "").toLowerCase().includes(q) ||
        String(r.client_name || "").toLowerCase().includes(q) ||
        String(r.client_manager_name || "").toLowerCase().includes(q) ||
        String(r.search_manager_name || "").toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [requests, search, status]);

  const handleCreated = (created) => {
    setRequests((prev) => [created, ...prev]);
    setCreateOpen(false);
  };

  return (
    <div className="proc-page">
      <div className="proc-page__header">
        <div>
          <div className="proc-breadcrumb">Заявки на поиск</div>
          <h1>Заявки на поиск</h1>
          <p>Поиск товаров по запросам клиентов</p>
        </div>

        {canManage && (
          <button
            type="button"
            className="proc-button"
            onClick={() => setCreateOpen(true)}
          >
            ＋ Создать заявку
          </button>
        )}
      </div>

      <div className="proc-toolbar">
        <div className="proc-filters">
          <input
            className="proc-input"
            placeholder="Поиск по номеру, товару, клиенту или менеджеру..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="proc-input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="proc-empty proc-empty--large">
          <h3>Загрузка заявок...</h3>
        </div>
      )}

      {!loading && error && (
        <div className="proc-empty proc-empty--large">
          <div className="proc-empty__icon">⚠️</div>
          <h3>{error}</h3>
          <button
            className="proc-button proc-button--secondary"
            onClick={load}
          >
            Повторить
          </button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="proc-results-info">
            <span>
              Найдено: <strong>{filtered.length}</strong>
            </span>
            <span>Всего: {requests.length}</span>
          </div>

          {filtered.length === 0 ? (
            <div className="proc-empty proc-empty--large">
              <div className="proc-empty__icon">🔎</div>
              <h3>Ничего не найдено</h3>
              <p>Попробуйте изменить фильтр или поиск.</p>
            </div>
          ) : (
            <div className="proc-table-wrap">
              <table className="proc-table">
                <thead>
                  <tr>
                    <th>Заявка</th>
                    <th>Товар</th>
                    <th>Клиент</th>
                    <th>Администратор</th>
                    <th>Менеджер по поиску</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/request-search/${r.id}`)}
                    >
                      <td>
                        <strong>{r.number}</strong>
                        <div className="proc-muted">ID: {r.id}</div>
                      </td>
                      <td>
                        {r.title || (
                          <span className="proc-muted">
                            {(r.description || "").slice(0, 60) || "—"}
                          </span>
                        )}
                      </td>
                      <td>
                        {r.client_name || (
                          <span className="proc-muted">—</span>
                        )}
                      </td>
                      <td>
                        {r.client_manager_name || (
                          <span className="proc-muted">
                            #{r.client_manager_id}
                          </span>
                        )}
                      </td>
                      <td>
                        {r.search_manager_name || (
                          <span className="proc-muted">
                            {r.search_manager_id
                              ? `#${r.search_manager_id}`
                              : "не назначен"}
                          </span>
                        )}
                      </td>
                      <td>
                        <StatusBadge status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {createOpen && (
        <CreateRequestModal
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}